/**
 * The affiliate portal shell — a sidebar-driven area inside the app, the way the
 * admin panel or a Stripe dashboard is laid out, rather than a single page.
 *
 * The shell also gates: only an approved partner sees the portal. Everyone else
 * gets the pitch and application form, or the notice for their status, rendered
 * full-width with no navigation to speak of.
 */
import { NavLink, Outlet, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { classNames } from '~/utils/classNames';
import { $currentUser } from '~/lib/stores/auth';
import { apiGetMyAffiliate, type Affiliate } from '~/lib/api/affiliates';
import { CARD, money } from './shared';
import { ApplyView, PendingNotice, RejectedNotice, SuspendedNotice } from './Apply';

const NAV = [
  { to: '/affiliates', end: true, icon: 'i-ph:squares-four', label: 'Overview' },
  { to: '/affiliates/referrals', icon: 'i-ph:users-three', label: 'Referrals' },
  { to: '/affiliates/earnings', icon: 'i-ph:receipt', label: 'Earnings' },
  { to: '/affiliates/payouts', icon: 'i-ph:bank', label: 'Payouts' },
  { to: '/affiliates/resources', icon: 'i-ph:book-open', label: 'Resources' },
];

export default function AffiliateLayout() {
  const { data, isLoading } = useQuery({
    queryKey: ['affiliate', 'me'],
    queryFn: apiGetMyAffiliate,
  });

  const affiliate = data?.affiliate ?? null;

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-5 py-10">
        <div className={classNames(CARD, 'h-24 animate-pulse')} />
      </div>
    );
  }

  // Before approval the rail is present but locked — an applicant can see what
  // the program opens up without being able to click into empty sections.
  if (!affiliate || affiliate.status !== 'approved') {
    return (
      <div className="flex w-full">
        <PortalSidebar affiliate={affiliate} unpaidCents={0} locked />

        <main className="min-w-0 flex-1 px-5 pb-24 pt-8 md:px-10">
          <div className="mx-auto w-full max-w-[900px]">
            <GateHeader status={affiliate?.status} />
            {!affiliate && <ApplyView />}
            {affiliate?.status === 'pending' && <PendingNotice />}
            {affiliate?.status === 'rejected' && <RejectedNotice />}
            {affiliate?.status === 'suspended' && <SuspendedNotice />}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex w-full">
      <PortalSidebar affiliate={affiliate} unpaidCents={data?.balance?.availableCents ?? 0} />

      <main className="min-w-0 flex-1 px-5 pb-24 pt-8 md:px-10">
        {/* The sidebar is desktop-only, so small screens get the same nav as a rail. */}
        <MobileNav affiliate={affiliate} unpaidCents={data?.balance?.availableCents ?? 0} />
        <div className="mx-auto w-full max-w-[1100px]">
          <Outlet context={{ data: data! }} />
        </div>
      </main>
    </div>
  );
}

/** The masthead shown to applicants — the portal's sidebar would be empty for them. */
function GateHeader({ status }: { status?: Affiliate['status'] }) {
  return (
    <header className="relative mb-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-24 mx-auto h-56 w-full max-w-xl"
        style={{
          background:
            'radial-gradient(50% 50% at 50% 50%, var(--accent-glow) 0%, rgba(217,242,106,0.05) 35%, transparent 70%)',
          filter: 'blur(48px)',
          opacity: 0.5,
        }}
      />

      <Link
        to="/"
        className="ui-caption relative inline-flex items-center gap-1.5 text-muted hover:text-secondary md:hidden"
      >
        <span className="i-ph:arrow-left text-[11px]" />
        Back to Dunea
      </Link>

      <h1 className="ui-serif relative mt-4 text-[34px] leading-[1.05] text-primary sm:text-[44px]">
        Get paid to share <em className="italic text-accent">Dunea</em>
      </h1>
      <p className="relative mt-3 max-w-xl text-[14px] leading-relaxed text-secondary">
        {status
          ? 'A curated partner program for creators, newsletters, and agencies.'
          : 'A curated program for creators, newsletters, and agencies. Refer a customer and earn a recurring cut of everything they pay.'}
      </p>
    </header>
  );
}

/**
 * Narrow-screen stand-in for the sidebar: the way home, the balance and referral
 * code (the numbers a partner opens the portal for), then the nav as a rail.
 */
function MobileNav({ affiliate, unpaidCents }: { affiliate: Affiliate; unpaidCents: number }) {
  return (
    <div className="mb-7 md:hidden">
      <Link to="/" className="ui-caption inline-flex items-center gap-1.5 py-2 text-muted hover:text-secondary">
        <span className="i-ph:arrow-left text-[11px]" />
        Back to Dunea
      </Link>

      <div className={classNames(CARD, 'mb-4 mt-2 flex items-center justify-between gap-4 px-4 py-3')}>
        <div className="min-w-0">
          <div className="ui-caption text-muted">Available</div>
          <div className="mt-1 text-[20px] font-medium leading-none text-accent">{money(unpaidCents)}</div>
        </div>
        <div className="min-w-0 text-right">
          <div className="ui-caption text-faint">Referral code</div>
          <div className="ui-mono mt-1 truncate text-[11.5px] text-muted">{affiliate.code}</div>
        </div>
      </div>

      <nav className="-mx-5 flex gap-1 overflow-x-auto px-5 pb-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              classNames(
                'flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 text-[12px] transition-colors touch:min-h-10',
                isActive ? 'border-subtle bg-sunken text-primary' : 'border-hairline text-secondary hover:text-primary',
              )
            }
          >
            <span className={classNames(item.icon, 'text-[13px]')} />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * The partner's account row — same shape as the app's main sidebar footer: a
 * bottom-pinned button with the lime avatar, their name, and a caption line.
 * Here the caption carries partner status and rate rather than plan and credits.
 */
function PartnerProfile({ affiliate }: { affiliate: Affiliate | null }) {
  const user = useStore($currentUser);
  const initial = (user?.displayName || user?.email || '?')[0].toUpperCase();

  const standing = !affiliate
    ? { label: 'Not a partner', tone: 'text-faint', detail: null }
    : affiliate.status === 'approved'
      ? { label: 'Partner', tone: 'text-accent', detail: `${affiliate.commissionRateBps / 100}%` }
      : affiliate.status === 'pending'
        ? { label: 'Under review', tone: 'text-warning', detail: null }
        : affiliate.status === 'rejected'
          ? { label: 'Not approved', tone: 'text-danger', detail: null }
          : { label: 'Suspended', tone: 'text-muted', detail: null };

  return (
    <div className="flex-shrink-0 border-t border-hairline py-2.5">
      {/* Identity, not a menu — the app's account dropdown lives in the main sidebar. */}
      <div className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left">
        <div
          className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent font-semibold text-page"
          style={{ width: 26, height: 26, fontSize: 11 }}
        >
          {user?.avatarUrl ? <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" /> : initial}
        </div>

        <div className="min-w-0 flex-1">
          <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] font-medium leading-tight tracking-[-0.005em] text-primary">
            {user?.displayName || user?.email || 'Account'}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className={classNames('ui-caption', standing.tone)}>{standing.label}</span>
            {standing.detail && (
              <>
                <span className="h-0.5 w-0.5 rounded-full bg-faint" />
                <span className="text-[11px] leading-none tabular-nums text-muted">{standing.detail}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Full-height rail mirroring the app's own sidebar: brand lockup at the top,
 * navigation in the middle, account pinned to the bottom.
 */
function PortalSidebar({
  affiliate,
  unpaidCents,
  locked,
}: {
  affiliate: Affiliate | null;
  unpaidCents: number;
  /** Pre-approval: the sections exist but nothing is reachable yet. */
  locked?: boolean;
}) {
  return (
    <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-hairline px-3 pt-8 md:flex">
      <div className="px-2">
        <Link to="/" className="flex items-center gap-2">
          <img src="/dunea-mark.svg" alt="" className="h-[22px] w-[22px]" />
          <span className="ui-brand text-[15px] text-primary">Dunea</span>
        </Link>
        <div className="ui-caption mt-2.5 text-faint">Partner portal</div>
      </div>

      <nav className="mt-6 flex flex-col gap-0.5">
        {NAV.map((item) =>
          locked ? (
            <div
              key={item.to}
              className="flex cursor-not-allowed items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-faint"
              title="Unlocks once your application is approved"
            >
              <span className={classNames(item.icon, 'text-[15px]')} />
              <span className="flex-1">{item.label}</span>
              <span className="i-ph:lock-simple text-[12px]" />
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                classNames(
                  'flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors',
                  isActive ? 'bg-sunken text-primary' : 'text-secondary hover:bg-white/[0.03] hover:text-primary',
                )
              }
            >
              <span className={classNames(item.icon, 'text-[15px]')} />
              {item.label}
            </NavLink>
          ),
        )}
      </nav>

      {/* Balance is the number a partner opens this portal to check. */}
      <div className={classNames(CARD, 'mt-6 p-4')}>
        <div className="ui-caption text-muted">Available</div>
        {locked ? (
          <>
            <div className="mt-1 text-[20px] font-medium leading-none text-faint">—</div>
            <div className="mt-2 text-[11px] leading-relaxed text-faint">Starts once you’re approved</div>
          </>
        ) : (
          <>
            <div className="mt-1 text-[20px] font-medium leading-none text-accent">{money(unpaidCents)}</div>
            <Link
              to="/affiliates/payouts"
              className="mt-3 inline-block text-[11.5px] text-secondary hover:text-primary"
            >
              Withdraw →
            </Link>
          </>
        )}
      </div>

      <div className="mt-5 px-2">
        <div className="ui-caption text-faint">Referral code</div>
        <div className="ui-mono mt-1 truncate text-[11.5px] text-muted" title={affiliate?.code ?? ''}>
          {affiliate?.code ?? 'Assigned on approval'}
        </div>
      </div>

      {/* Spacer pushes the account row to the bottom, as in the main sidebar. */}
      <div className="flex-1" />

      <PartnerProfile affiliate={affiliate} />
    </aside>
  );
}
